import * as ed from '@noble/ed25519';

import { canonicalize } from '../license/canonical.js';

/**
 * Signed bundle manifest.
 *
 * A launcher bundle (template ZIP + per-tenant config/license + assets)
 * is assembled client-side today, which means nothing stops a tampered
 * config.json or a swapped-in higher-tier license from being side-loaded.
 * The signed manifest closes that: the server lists each integrity-
 * critical file with its SHA-256 and signs the whole manifest with the
 * same Ed25519 key that signs licenses. On device, the renderer verifies
 * the signature (public key embedded) and re-hashes each file — a
 * mismatch means the bundle was altered after the server built it.
 *
 * Canonicalization + signing reuse the license machinery so signer
 * (Worker) and verifier (renderer) produce identical bytes.
 */

/** One file covered by the manifest, by ZIP path + hex SHA-256. */
export interface BundleFileRef {
  path: string;
  sha256: string;
}

export interface UnsignedBundleManifest {
  tenantId: string;
  tier: 'core' | 'pro' | 'enterprise';
  /** Launcher template version the bundle was built against. */
  templateRef: string;
  /** Epoch seconds the manifest was built/signed. */
  builtAt: number;
  /** Integrity-critical files, sorted by path for determinism. */
  files: BundleFileRef[];
}

export interface SignedBundleManifest extends UnsignedBundleManifest {
  /** Hex Ed25519 signature over the canonicalized unsigned manifest. */
  signature: string;
}

export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0) throw new Error('hex string must have an even length');
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) {
    const byte = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    if (Number.isNaN(byte)) throw new Error('invalid hex');
    out[i] = byte;
  }
  return out;
}

/** Canonical bytes that get signed/verified — the manifest minus its signature. */
export function bundleManifestMessageBytes(
  manifest: UnsignedBundleManifest | SignedBundleManifest,
): Uint8Array {
  const obj: Record<string, unknown> = { ...manifest };
  delete obj['signature'];
  return new TextEncoder().encode(canonicalize(obj));
}

/** Hex SHA-256 of a file's contents (Web Crypto; available in Workers, Node, browsers). */
export async function sha256Hex(content: string | Uint8Array): Promise<string> {
  const bytes = typeof content === 'string' ? new TextEncoder().encode(content) : content;
  // Cast: TextEncoder/Uint8Array are typed over ArrayBufferLike, but
  // crypto.subtle.digest wants an ArrayBuffer-backed view (always true here).
  const digest = await crypto.subtle.digest('SHA-256', bytes as Uint8Array<ArrayBuffer>);
  return ed.etc.bytesToHex(new Uint8Array(digest));
}

/** Hash each file and produce a deterministic (path-sorted) unsigned manifest. */
export async function buildUnsignedBundleManifest(input: {
  tenantId: string;
  tier: UnsignedBundleManifest['tier'];
  templateRef: string;
  builtAt: number;
  files: { path: string; content: string | Uint8Array }[];
}): Promise<UnsignedBundleManifest> {
  const files: BundleFileRef[] = await Promise.all(
    input.files.map(async (f) => ({ path: f.path, sha256: await sha256Hex(f.content) })),
  );
  files.sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
  return {
    tenantId: input.tenantId,
    tier: input.tier,
    templateRef: input.templateRef,
    builtAt: input.builtAt,
    files,
  };
}

/** Sign an unsigned manifest with a 32-byte Ed25519 private key. */
export async function signBundleManifest(
  unsigned: UnsignedBundleManifest,
  privateKey: Uint8Array,
): Promise<SignedBundleManifest> {
  const sig = await ed.signAsync(bundleManifestMessageBytes(unsigned), privateKey);
  return { ...unsigned, signature: ed.etc.bytesToHex(sig) };
}

/** Verify the manifest's signature against the issuer's public key. */
export async function verifyBundleSignature(
  manifest: SignedBundleManifest,
  publicKey: Uint8Array,
): Promise<boolean> {
  try {
    return await ed.verifyAsync(
      hexToBytes(manifest.signature),
      bundleManifestMessageBytes(manifest),
      publicKey,
    );
  } catch {
    return false;
  }
}

/**
 * Confirm a file's actual contents match the hash the manifest recorded.
 * Returns false when the path isn't covered or the hash differs.
 */
export async function verifyBundleFile(
  manifest: SignedBundleManifest,
  path: string,
  content: string | Uint8Array,
): Promise<boolean> {
  const ref = manifest.files.find((f) => f.path === path);
  if (ref === undefined) return false;
  return (await sha256Hex(content)) === ref.sha256;
}
